if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        /* K+K resilience patch: the Optis / BSS Product Options app can
           relocate or rebuild this element's <form> before or after the
           custom element upgrades. The stock constructor crashed on the
           missing form ("Cannot read properties of null"), which left the
           element un-upgraded and broke variant selection (default variant
           added to cart). Instead of assuming the form is present, we
           initialize lazily and retry briefly until the form exists —
           searching by the form's id anywhere in the document if it was
           moved outside this element. */
        this.kkInitialized = false;
        this.kkTryInit();
        if (!this.kkInitialized) {
          let attempts = 0;
          const timer = setInterval(() => {
            attempts++;
            this.kkTryInit();
            if (this.kkInitialized || attempts > 100) clearInterval(timer);
          }, 100);
        }
        this.kkOptisSetup();
      }

      /* ---- K+K OPTIS gate (v2.3) ------------------------------------------
         Products tagged 'optis-options' carry an OPTIS option set. OPTIS only
         validates clicks on its own button (.po-atc-btn) and then adds through
         HTMLFormElement.prototype.submit (its own path). The theme form can
         still be submitted by Enter in the quantity box, by scripts, or before
         OPTIS has rendered - those adds carry no options. This gate makes every
         theme-side submit (event or form.submit()) go through OPTIS or nowhere:
           * not rendered yet  -> hold (button disabled server-side, express hidden)
           * rendered          -> route the submit into OPTIS's button and block
           * never renders     -> keep held, tell the customer, keep watching
           * rendered then gone (OPTIS rebuild) -> hold again until it is back
         Express checkout is shown only once OPTIS has hooked the Buy-it-now
         button (it validates that click itself). Every lookup is scoped to this
         product's form, never the whole page. A tagged product in a quick-add
         card never adds from the card; it opens the product page instead. */
      kkOptisSetup() {
        if (this.kkOptis || this.dataset.kkOptis !== '1') return;
        this.kkOptis = true;
        this.kkInjectStyles();
        if (this.dataset.kkUrl) {                      /* quick-add card */
          this.kkCard = true;
          /* the parser upgrades this element before its children exist, so
             wait for the held button, then turn it into a "Choose options" link */
          let tries = 0;
          const arm = () => {
            const btn = this.querySelector('[data-kk-hold="1"]');
            if (btn) {
              btn.removeAttribute('disabled');
              btn.removeAttribute('aria-disabled');
              const span = btn.querySelector('span');
              if (span) span.textContent = 'Choose options';
              return;
            }
            if (++tries < 100) setTimeout(arm, 100);
          };
          arm();
          return;
        }
        this.classList.add('kk-optis', 'kk-optis-pending');
        this.kkOptisSay('Loading product options\u2026', 'info');
        /* Any submit event from ANY form inside this element (a rebuilt one included)
           is stopped here first; the bound form's own handler then applies the gate. */
        this.addEventListener('submit', (evt) => {
          evt.preventDefault();
          this.kkBindLiveForm();
          if (evt.target !== this.form) this.kkOptisGate();   /* unbound form: gate it right now */
        }, true);
        const started = Date.now();
        const LIMIT_MS = 12000;
        const tick = () => {
          /* re-bind to the live form every tick: OPTIS or section rendering may have
             replaced the <form>; the hold is released only for a bound + guarded form */
          const bound = this.kkBindLiveForm();
          const rendered = bound && this.kkOptisIsRendered();
          if (rendered) {
            if (!this.classList.contains('kk-optis-ready')) {
              this.classList.remove('kk-optis-pending', 'kk-optis-fail');
              this.classList.add('kk-optis-ready');
              this.kkLostAt = null;
              this.kkReleaseHold();
              this.kkOptisSay('', 'info');
            }
            this.classList.toggle('kk-optis-express-ok', this.kkExpressHooked());
          } else {
            if (this.classList.contains('kk-optis-ready')) {   /* OPTIS block went away (rebuild) */
              this.classList.remove('kk-optis-ready', 'kk-optis-express-ok');
              this.classList.add('kk-optis-pending');
              this.kkLostAt = Date.now();
              this.kkHold();
              this.kkOptisSay('Loading product options\u2026', 'info');
            }
            const since = this.kkLostAt || started;
            if (Date.now() - since > LIMIT_MS && !this.classList.contains('kk-optis-fail')) {
              this.classList.remove('kk-optis-pending', 'kk-optis-express-ok');
              this.classList.add('kk-optis-fail');
              this.kkHold();
              this.kkOptisSay(this.kkFailText(), 'fail');
            }
          }
          setTimeout(tick, this.classList.contains('kk-optis-fail') ? 1000 : (rendered ? 500 : 250));   /* watch for the life of the page */
        };
        tick();
      }

      kkFailText() {
        return 'Product options did not load. Please refresh the page, or call (305) 317-2619 and we will take your order.';
      }

      /* the live form (OPTIS may move it out of this element), else this element */
      kkRoot() {
        return (this.form && this.form.isConnected) ? this.form : this;
      }

      /* where OPTIS's button, the express button and our held button live for THIS
         product: the form and this element. Never the whole page (a quick-add modal
         must not find the main product's OPTIS button). */
      kkScope() {
        const root = this.kkRoot();
        return root === this ? [this] : [root, this];
      }

      kkFind(selector) {
        for (const root of this.kkScope()) {
          const el = root.querySelector(selector);
          if (el) return el;
        }
        return null;
      }

      kkOptisIsRendered() {
        if (window.__kkOptisNoRender) return false;    /* test hook: needs script injection, not reachable by URL */
        return !!this.kkFind('.po-atc-btn');
      }

      /* OPTIS marks the dynamic checkout button once it has attached its own validation to it */
      kkExpressHooked() {
        const pay = this.kkFind('.shopify-payment-button');
        return !!pay && pay.hasAttribute('po-btn-buy-it-now');
      }

      kkHoldButtons() {
        const set = new Set();
        for (const root of this.kkScope()) root.querySelectorAll('[data-kk-hold="1"]').forEach((b) => set.add(b));
        return [...set];
      }

      kkHold() {
        this.kkHoldButtons().forEach((btn) => { btn.setAttribute('disabled', 'disabled'); btn.setAttribute('aria-disabled', 'true'); });
      }

      kkReleaseHold() {
        this.kkHoldButtons().forEach((btn) => { btn.removeAttribute('disabled'); btn.removeAttribute('aria-disabled'); });
      }

      /* Re-attach to whatever form is live right now. Returns true only when the live
         form has our submit listener AND the instance-submit guard. */
      kkBindLiveForm() {
        this.kkEnsureLive();
        if (!this.kkInitialized) this.kkTryInit();
        this.kkGuardInstanceSubmit();
        return !!(this.kkInitialized && this.form && this.form.isConnected && this.form.kkGuarded);
      }

      /* form.submit() (the instance method) fires no submit event. OPTIS adds through
         HTMLFormElement.prototype.submit and never through the instance method (measured:
         0 instance calls / 3 prototype calls on a valid add), so gating the instance
         method catches other scripts without touching OPTIS. */
      kkGuardInstanceSubmit() {
        const form = this.form;
        if (!form || form.kkGuarded) return;
        form.kkGuarded = true;
        const self = this;
        form.submit = function () {
          if (!self.kkOptisGate()) HTMLFormElement.prototype.submit.call(form);
        };
      }

      kkInjectStyles() {
        if (document.getElementById('kk-optis-style')) return;
        const st = document.createElement('style');
        st.id = 'kk-optis-style';
        st.textContent = '.kk-optis:not(.kk-optis-express-ok) .shopify-payment-button{visibility:hidden!important}'
          + '.kk-optis-fail .shopify-payment-button{display:none!important}'
          + '.kk-optis-gate{margin:.6rem 0 .4rem;font-size:.95rem;line-height:1.4}'
          + '.kk-optis-gate[data-tone="fail"]{color:#b3392e;font-weight:600}';
        document.head.appendChild(st);
      }

      kkOptisSay(text, tone) {
        let el = this.querySelector('.kk-optis-gate');
        if (!el) {
          el = document.createElement('div');
          el.className = 'kk-optis-gate';
          el.setAttribute('role', 'status');
          el.setAttribute('aria-live', 'polite');
          this.insertBefore(el, this.firstChild);
        }
        el.textContent = text;
        el.setAttribute('data-tone', tone || 'info');
        el.hidden = !text;
      }

      /* Returns true when the theme form must NOT add on its own. On a gated
         product page that is always: the only way into the cart is OPTIS's own
         validated path. Decided live on every call; never trusts a cached flag. */
      kkOptisGate() {
        if (!this.kkOptis) return false;
        if (this.kkCard) {                              /* quick-add card: go choose options */
          if (this.dataset.kkUrl) window.location.href = this.dataset.kkUrl;
          return true;
        }
        const optisBtn = window.__kkOptisNoRender ? null : this.kkFind('.po-atc-btn');
        if (optisBtn) {
          const now = Date.now();
          if (!this.kkOptisClickedAt || now - this.kkOptisClickedAt > 1500) {
            this.kkOptisClickedAt = now;
            try {
              optisBtn.click();                         /* OPTIS validates; when valid it adds via its own path */
            } catch (e) {
              console.error('[KK OPTIS gate] could not hand off to OPTIS:', e);
              this.kkOptisSay(this.kkFailText(), 'fail');
            }
          }
          return true;                                  /* the theme form itself never adds */
        }
        const failed = this.classList.contains('kk-optis-fail');
        this.kkOptisSay(failed ? this.kkFailText() : 'Product options are still loading \u2014 one moment, then try again.', failed ? 'fail' : 'info');
        return true;
      }

      kkEnsureLive() {
        /* If Optis replaced (not just moved) the form after we initialized,
           our captured reference points at a detached node. Detect and re-init
           against the live form. */
        if (this.kkInitialized && this.form && this.form.isConnected === false) {
          this.kkInitialized = false;
          this.form = null;
          this.submitButton = null;
          this.submitButtonText = null;
          this.kkTryInit();
        }
      }

      kkResolveCart() {
        /* BSS PO's cart observer can break the cart-drawer / cart-notification
           upgrade the same way it broke this element's. Only accept a cart
           element that actually has the methods we call; otherwise behave as
           if there is no AJAX cart (stock Dawn then redirects to /cart). */
        const candidates = document.querySelectorAll('cart-notification, cart-drawer');
        for (const c of candidates) {
          if (typeof c.getSectionsToRender === 'function' && typeof c.renderContents === 'function') return c;
        }
        return null;
      }

      kkFindForm() {
        return (
          this.querySelector('form') ||
          (this.dataset.sectionId && document.getElementById(`product-form-${this.dataset.sectionId}`)) ||
          null
        );
      }

      kkTryInit() {
        if (this.kkInitialized) return;
        const form = this.kkFindForm();
        if (!form) return;
        const submitButton = this.querySelector('[type="submit"]') || form.querySelector('[type="submit"]');
        const submitButtonText = submitButton && submitButton.querySelector('span');
        if (!form.querySelector('[name=id]') || !submitButton || !submitButtonText) return;

        this.form = form;
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = this.kkResolveCart();
        this.submitButton = submitButton;
        this.submitButtonText = submitButtonText;

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.kkInitialized = true;
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (!this.kkInitialized) this.kkTryInit();
        this.kkEnsureLive();
        if (!this.submitButton) return;
        if (this.kkOptisGate()) return;
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        try {
          this.kkDoSubmit();
        } catch (error) {
          /* Never strand the customer on a stuck spinner. Reset the button
             and fall back to a plain (non-AJAX) form submission, which adds
             to cart via full page load no matter what apps broke. */
          console.error('[KK product-form] AJAX add-to-cart failed, falling back to native submit:', error);
          this.kkResetButtonState();
          if (this.form && !this.kkOptisGate()) HTMLFormElement.prototype.submit.call(this.form);
        }
      }

      kkResetButtonState() {
        if (!this.submitButton) return;
        this.submitButton.classList.remove('loading');
        this.submitButton.removeAttribute('aria-disabled');
        (this.querySelector('.loading__spinner') || (this.form && this.form.querySelector('.loading__spinner')))?.classList.add('hidden');
      }

      kkDoSubmit() {

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        (this.querySelector('.loading__spinner') || this.form.querySelector('.loading__spinner'))?.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        this.cart = this.kkResolveCart();
        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          if (typeof this.cart.setActiveElement === 'function') this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            (this.querySelector('.loading__spinner') || this.form.querySelector('.loading__spinner'))?.classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.kkInitialized) this.kkTryInit();
        this.kkEnsureLive();
        if (!this.submitButton || !this.submitButtonText) return;
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          if (!(this.kkOptis && !this.kkCard && !this.classList.contains('kk-optis-ready'))) this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
          /* Self-heal: if a previous submit crashed mid-flight, clear the
             stuck loading/spinner state when the button is re-enabled. */
          this.kkResetButtonState();
        }
      }

      get variantIdInput() {
        if (!this.form) this.kkTryInit();
        this.kkEnsureLive();
        return this.form ? this.form.querySelector('[name=id]') : null;
      }
    }
  );
}
